import { z } from 'zod';
import { CurrencySchema } from './stores.js';
import { offerSearchText } from './promotions.js';
import { MAX_PRICE_MINOR, MAX_QTY } from './money.js';
import { BarcodesSchema } from './barcodes.js';
/**
 * Sablona — veci, ktere skupina kupuje pravidelne. Zaklada si ji sama a pak
 * z ni pri planovani velkeho nakupu jen vybira, co a kolik chce tentokrat.
 * Nahrazuje ten Wordovsky dokument, ze ktereho sestra dosud kopirovala.
 */
export const CatalogItemSchema = z.object({
    id: z.uuid(),
    groupId: z.uuid(),
    name: z.string().min(1).max(200),
    category: z.string().max(80).nullable(),
    /** null = obchod neurceny; polozka se pri planovani zaradi do prvniho obchodu. */
    storeId: z.uuid().nullable(),
    defaultQty: z.number().positive().max(MAX_QTY),
    unit: z.string().max(20).nullable(),
    note: z.string().max(500).nullable(),
    /** U casti nakupu se cena nehlida vubec — pak zustava null. */
    typicalPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable(),
    typicalCurrency: CurrencySchema.nullable(),
    /**
     * Jak polozku hledat v akcich z letaku, oddelene carkou („Butter, Masło").
     * Nazvy v letacich jsou polsky a nemecky; bez vyrazu se zkusi cesky nazev.
     */
    offerTerms: z.string().max(300).nullable(),
    /** Carove kody baleni (viz barcodes.ts). Default kvuli seznamu ulozenemu v telefonu starsi verzi. */
    barcodes: z.array(z.string()).default([]),
    archived: z.boolean(),
    lastBoughtAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * Zapisovatelna pole BEZ vychozich hodnot.
 *
 * Patch se musi odvozovat odsud, ne ze schematu s `.default()`. Zod 4
 * dosazuje vychozi hodnoty i u poli, ktera v patchi chybi, takze
 * `PATCH { name }` tise prepsal kategorii, jednotku i cenu na null a mnozstvi
 * na 1. Chytil to smoke test proti databazi, test v test/catalog.test.ts
 * hlida, aby se to nevratilo.
 */
const writableFields = {
    name: z.string().trim().min(1).max(200),
    category: z.string().trim().max(80).nullable(),
    storeId: z.uuid().nullable(),
    defaultQty: z.number().positive().max(MAX_QTY),
    unit: z.string().trim().max(20).nullable(),
    note: z.string().max(500).nullable(),
    typicalPriceMinor: z.number().int().nonnegative().max(MAX_PRICE_MINOR).nullable(),
    typicalCurrency: CurrencySchema.nullable(),
    offerTerms: z.string().trim().max(300).nullable(),
    barcodes: BarcodesSchema,
};
export const CatalogItemInputSchema = z.object({
    ...writableFields,
    category: writableFields.category.default(null),
    storeId: writableFields.storeId.default(null),
    defaultQty: writableFields.defaultQty.default(1),
    unit: writableFields.unit.default(null),
    note: writableFields.note.default(null),
    typicalPriceMinor: writableFields.typicalPriceMinor.default(null),
    typicalCurrency: writableFields.typicalCurrency.default(null),
    offerTerms: writableFields.offerTerms.default(null),
    barcodes: writableFields.barcodes.default([]),
});
export const CatalogItemPatchSchema = z
    .object(writableFields)
    .partial()
    .extend({ archived: z.boolean().optional() });
export const CatalogResponseSchema = z.object({
    items: z.array(CatalogItemSchema),
});
/**
 * Kopie polozek sablony z jine skupiny (sestra si sablonu pripravi v osobni
 * skupine a zkopiruje ji do spolecne). Jednorazova — dalsi zmeny se mezi
 * sablonami neprenaseji.
 */
export const CatalogCopySchema = z.object({
    fromGroupId: z.uuid(),
    itemIds: z.array(z.uuid()).min(1).max(1000),
    /** Kopiruje se sablona jina nez Pravidelny nakup: v cilove skupine vznikne stejnojmenna. */
    fromTemplateId: z.uuid().optional(),
});
export const CatalogCopyResponseSchema = z.object({
    created: z.array(CatalogItemSchema),
    /** Polozky, ktere v cilove sablone uz byly (stejny nazev). */
    skipped: z.number().int().nonnegative(),
    /** Obchody, ktere v cilove skupine chybely a zalozily se jako kopie. */
    createdStores: z.array(z.string()),
});
/** Nazev polozky pro porovnani mezi sablonami: bez diakritiky, velikosti pismen a interpunkce. */
export function catalogNameKey(name) {
    return offerSearchText(name);
}
/**
 * Obchod cilove skupiny pro polozku z jine sablony: stejna mena a k tomu stejny
 * nazev, jinak stejny retezec akci („Biedronka Bogatynia" a „Biedronka").
 * null = v cilove skupine takovy obchod neni a pri kopii se zalozi.
 */
export function suggestStore(source, targets) {
    // Stejna mena, at cena polozky dal dava smysl.
    const active = targets.filter((t) => !t.archived && t.currency === source.currency);
    const key = catalogNameKey(source.name);
    const byName = active.find((t) => catalogNameKey(t.name) === key);
    if (byName)
        return byName;
    if (!source.offerChain)
        return null;
    return active.find((t) => t.offerChain === source.offerChain && t.currency === source.currency) ?? null;
}
//# sourceMappingURL=catalog.js.map